import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Calendar } from "./ui/calendar";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { MessageCircle, CreditCard, QrCode, Loader2, CheckCircle2 } from "lucide-react";

const QR_URL =
  "https://customer-assets.emergentagent.com/job_aroha-rooms/artifacts/qcsbiuz8_PHOTO-2026-05-16-02-07-08.jpg";

function toISO(d) {
  if (!d) return null;
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${yr}-${mo}-${da}`;
}

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function BookingDialog({ room, open, onOpenChange }) {
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [unavailable, setUnavailable] = useState([]);
  const [config, setConfig] = useState({ razorpay_enabled: false, advance_percent: 25, whatsapp_number: "", payment: {} });
  const [form, setForm] = useState({ guest_name: "", guest_email: "", guest_phone: "", guests: 2, notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    if (!open) {
      setRange({ from: undefined, to: undefined });
      setBooking(null);
      setForm({ guest_name: "", guest_email: "", guest_phone: "", guests: 2, notes: "" });
    }
  }, [open]);

  useEffect(() => {
    api.get("/config/public").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!room) return;
    api
      .get(`/rooms/${room.id}/availability`, { params: { months: 5 } })
      .then((r) => setUnavailable((r.data.unavailable_dates || []).map((s) => new Date(s + "T00:00:00"))))
      .catch(() => setUnavailable([]));
  }, [room]);

  if (!room) return null;

  const nights = range.from && range.to ? Math.max(0, Math.round((range.to - range.from) / 86400000)) : 0;
  const total = nights * room.price_per_night;
  const advance = nights > 0 ? Math.max(1, Math.round((total * config.advance_percent) / 100)) : 0;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const isUnavailable = (d) => unavailable.some((u) => u.getTime() === d.getTime()) || d < today;

  const whatsappLink = () => {
    const n = config.whatsapp_number || "919353480980";
    const dates = range.from && range.to ? ` for ${toISO(range.from)} to ${toISO(range.to)}` : "";
    const text = encodeURIComponent(
      `Hi Aroha Homes! I'd like to enquire about ${room.name}${dates}. My name is ${form.guest_name || "—"}.`
    );
    return `https://wa.me/${n}?text=${text}`;
  };

  const submitBooking = async () => {
    if (!range.from || !range.to) return toast.error("Pick check-in and check-out dates");
    if (nights < 1) return toast.error("Stay must be at least 1 night");
    if (!form.guest_name.trim()) return toast.error("Please share your name");
    if (!form.guest_email.trim()) return toast.error("Please share your email");
    if (!form.guest_phone.trim()) return toast.error("Please share your phone");
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        room_id: room.id,
        check_in: toISO(range.from),
        check_out: toISO(range.to),
        guests: Number(form.guests) || 1,
        guest_name: form.guest_name,
        guest_email: form.guest_email,
        guest_phone: form.guest_phone,
        notes: form.notes,
      });
      setBooking(data);
      toast.success("Booking created. Complete the advance payment to confirm.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to create booking");
    } finally {
      setSubmitting(false);
    }
  };

  const payNow = async () => {
    if (!booking) return;
    if (!config.razorpay_enabled || !booking.razorpay_order_id) {
      toast.message("Online checkout not configured", {
        description: "Please use UPI QR / Bank Transfer below and confirm on WhatsApp.",
      });
      return;
    }
    const ok = await loadRazorpay();
    if (!ok) return toast.error("Could not load Razorpay");
    const options = {
      key: window.__RZP_KEY__ || "",
      amount: booking.advance_amount * 100,
      currency: "INR",
      name: "Aroha Homes",
      description: `${booking.room_name} · ${booking.nights} night(s) advance`,
      order_id: booking.razorpay_order_id,
      prefill: {
        name: booking.guest_name,
        email: booking.guest_email,
        contact: booking.guest_phone,
      },
      theme: { color: "#B85B3F" },
      handler: async (resp) => {
        try {
          await api.post("/bookings/verify-payment", {
            booking_id: booking.id,
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          setBooking({ ...booking, status: "advance_paid", razorpay_payment_id: resp.razorpay_payment_id });
          toast.success("Advance paid — your booking is confirmed!");
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Payment verification failed");
        }
      },
    };
    // public key from config
    options.key = config.razorpay_key_id;
    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="booking-dialog"
        className="max-w-4xl max-h-[92vh] overflow-y-auto bg-card border-border"
      >
        <DialogHeader>
          <p className="overline">{room.name}</p>
          <DialogTitle className="font-display text-3xl font-medium">Check Availability & Book</DialogTitle>
          <DialogDescription className="font-sans text-sm text-muted-foreground">
            Pick your check-in and check-out dates. Greyed dates are unavailable.
            Confirm with a {config.advance_percent}% advance; balance is paid at check-in.
          </DialogDescription>
        </DialogHeader>

        {!booking ? (
          <div className="grid md:grid-cols-[auto_1fr] gap-8 mt-4">
            <div className="bg-background rounded-2xl border border-border p-3 self-start">
              <Calendar
                mode="range"
                selected={range}
                onSelect={(r) => setRange(r || { from: undefined, to: undefined })}
                numberOfMonths={1}
                disabled={isUnavailable}
                data-testid="booking-calendar"
              />
            </div>

            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Check-in</Label>
                  <div data-testid="checkin-display" className="font-display text-xl text-foreground mt-1">
                    {range.from ? toISO(range.from) : "—"}
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Check-out</Label>
                  <div data-testid="checkout-display" className="font-display text-xl text-foreground mt-1">
                    {range.to ? toISO(range.to) : "—"}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="g-name">Full name</Label>
                  <Input
                    id="g-name"
                    data-testid="input-name"
                    value={form.guest_name}
                    onChange={(e) => setForm({ ...form, guest_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="g-guests">Guests</Label>
                  <Input
                    id="g-guests"
                    type="number"
                    min={1}
                    max={room.max_guests}
                    data-testid="input-guests"
                    value={form.guests}
                    onChange={(e) => setForm({ ...form, guests: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="g-email">Email</Label>
                  <Input
                    id="g-email"
                    type="email"
                    data-testid="input-email"
                    value={form.guest_email}
                    onChange={(e) => setForm({ ...form, guest_email: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="g-phone">Phone</Label>
                  <Input
                    id="g-phone"
                    data-testid="input-phone"
                    value={form.guest_phone}
                    onChange={(e) => setForm({ ...form, guest_phone: e.target.value })}
                  />
                </div>
              </div>

              <div className="rounded-2xl bg-accent/40 p-5 mt-2 font-sans text-sm">
                <div className="flex justify-between">
                  <span>₹{room.price_per_night.toLocaleString("en-IN")} × {nights || 0} night(s)</span>
                  <span data-testid="summary-total">₹{total.toLocaleString("en-IN")}</span>
                </div>
                <div className="flex justify-between mt-2 text-primary font-semibold">
                  <span>{config.advance_percent}% advance to confirm</span>
                  <span data-testid="summary-advance">₹{advance.toLocaleString("en-IN")}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">Balance ₹{(total - advance).toLocaleString("en-IN")} at check-in</div>
              </div>

              <div className="flex gap-3">
                <Button
                  data-testid="confirm-booking-btn"
                  disabled={submitting || nights < 1}
                  className="rounded-full bg-primary hover:bg-primary/90 px-6"
                  onClick={submitBooking}
                >
                  {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Continue to Payment
                </Button>
                <Button
                  data-testid="whatsapp-enquiry-btn"
                  variant="outline"
                  className="rounded-full"
                  asChild
                >
                  <a href={whatsappLink()} target="_blank" rel="noreferrer">
                    <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp Enquiry
                  </a>
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <PaymentStep
            booking={booking}
            config={config}
            payNow={payNow}
            whatsappLink={whatsappLink()}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function PaymentStep({ booking, config, payNow, whatsappLink }) {
  const paid = booking.status === "advance_paid";
  return (
    <div className="mt-4 space-y-6" data-testid="payment-step">
      {paid && (
        <div className="rounded-2xl bg-secondary/10 border border-secondary/30 p-5 flex items-center gap-3 text-secondary" data-testid="paid-banner">
          <CheckCircle2 className="w-6 h-6" /> Advance received. Your booking is confirmed. We'll send a WhatsApp confirmation.
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-border p-6 bg-background/50">
          <div className="overline mb-3">Booking Summary</div>
          <div className="font-display text-2xl mb-1">{booking.room_name}</div>
          <div className="text-sm text-muted-foreground">{booking.check_in} → {booking.check_out} · {booking.nights} night(s) · {booking.guests} guest(s)</div>
          <div className="mt-4 text-sm">Total: <strong>₹{booking.total_amount.toLocaleString("en-IN")}</strong></div>
          <div className="text-primary font-semibold">Advance now: ₹{booking.advance_amount.toLocaleString("en-IN")}</div>
        </div>

        <div className="rounded-2xl border border-border p-6 bg-background/50">
          <div className="overline mb-3">Pay Advance</div>
          {config.razorpay_enabled && booking.razorpay_order_id ? (
            <Button
              data-testid="razorpay-pay-btn"
              onClick={payNow}
              disabled={paid}
              className="w-full rounded-full bg-primary hover:bg-primary/90"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              {paid ? "Paid" : `Pay ₹${booking.advance_amount.toLocaleString("en-IN")} via Razorpay`}
            </Button>
          ) : (
            <div className="text-sm text-muted-foreground">
              Online checkout is not configured yet. Please pay via UPI / Bank Transfer below and confirm
              on WhatsApp — we'll mark your booking as confirmed within minutes.
            </div>
          )}
          <Button asChild variant="outline" className="mt-3 w-full rounded-full">
            <a href={whatsappLink} target="_blank" rel="noreferrer" data-testid="payment-whatsapp-btn">
              <MessageCircle className="w-4 h-4 mr-2" /> Confirm on WhatsApp
            </a>
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-border p-6 bg-background/50">
        <div className="overline mb-4 flex items-center gap-2"><QrCode className="w-4 h-4" /> UPI · Bank Transfer</div>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="text-sm font-sans space-y-1">
            <div><span className="text-muted-foreground">Account holder:</span> <strong>{config.payment?.bank_account_holder}</strong></div>
            <div><span className="text-muted-foreground">Bank:</span> {config.payment?.bank_name}</div>
            <div><span className="text-muted-foreground">A/C:</span> {config.payment?.bank_account_number}</div>
            <div><span className="text-muted-foreground">IFSC:</span> {config.payment?.bank_ifsc}</div>
            <div><span className="text-muted-foreground">Branch:</span> {config.payment?.bank_branch}</div>
            <div className="pt-2"><span className="text-muted-foreground">UPI:</span> <strong>{config.payment?.upi_id}</strong></div>
          </div>
          <div className="flex justify-center items-center">
            <img src={QR_URL} alt="UPI QR" data-testid="upi-qr-image" className="rounded-xl border border-border max-w-[220px]" />
          </div>
        </div>
      </div>
    </div>
  );
}
